import { CONTENT_FITNESS_ASSESSMENT_MODE } from "../../../enums/content-fitness";
import { CONTENT_FITNESS_FRAMEWORK_VERSION } from "../version";
import { anchors, type ContentFitnessSubcriterion, deferred } from "./types";

export const HUMAN_FIRST_CONTENT_QUALITY_SUBCRITERIA = [
  {
    key: "human_first_content_quality.content_value_substance",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Content value & substance",
    parentMetricKey: "human_first_content_quality",
    order: 1,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    pilot: true,
    definition:
      "Scores whether the page delivers distinctive, useful, current and authoritative content for the intended human audience.",
    evidenceGuidance:
      "Use body text, page type, last-modified signals, claims, proof points, citations, testimonials, product/service detail, policy detail, and evidence of genericity or thinness.",
    applicabilityNotes:
      "Applies to almost all indexable content pages. For utility pages with intentionally minimal copy, score against the content's actual job rather than length. Do not claim external uniqueness or historical freshness without evidence.",
    anchors: anchors(
      "No meaningful substantive value; empty, misleading, unusable or fundamentally unfit for the page's apparent purpose.",
      "Mostly thin, generic, stale, unsupported or derivative, with little distinctive value for the intended audience.",
      "Some useful substance is present, but material gaps in depth, specificity, currency, authority or proof limit confidence.",
      "Strong and genuinely useful overall, with only contained gaps in depth, freshness, specificity or support.",
      "Distinctive, substantive, current and evidence-backed on the page; provides clear value beyond generic or templated content.",
    ),
    objectiveChecks: [
      "last-modified signal present when relevant",
      "visible citations/proof present where claims require support",
      "empty or near-empty body text",
    ],
  },
  {
    key: "human_first_content_quality.clarity_language",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Clarity & language",
    parentMetricKey: "human_first_content_quality",
    order: 2,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.RUBRIC,
    pilot: true,
    definition:
      "Scores whether the writing is plain, concise, correct, natural and understandable for the intended human reader.",
    evidenceGuidance:
      "Use body text, headings, paragraph copy, reading-level signals, jargon, undefined acronyms, grammar, punctuation and active/passive construction.",
    applicabilityNotes:
      "Applies to human-readable copy. Minimal navigation or system pages may have limited applicable evidence; state the assumed audience when it is inferred.",
    anchors: anchors(
      "Copy is absent, broken, incoherent, misleading or unusable for the intended reader.",
      "Meaning is frequently obscured by jargon, verbosity, errors, fragments or unexplained terms.",
      "The page is understandable in parts, but recurring language, structure or correctness issues slow or confuse the reader.",
      "Clear, plain and correct overall, with only minor local issues or audience-specific terms that remain understandable.",
      "Exceptionally clear, concise and natural; the language helps the reader understand complex material without oversimplifying it.",
    ),
    objectiveChecks: [
      "spelling/typo scan",
      "undefined acronym detection",
      "extreme sentence length",
      "placeholder/lorem ipsum text",
    ],
  },
  deferred(
    "human_first_content_quality.access_responsiveness",
    "Access & responsiveness",
    "human_first_content_quality",
    3,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether the intended audience can reach, use and get help from the content in a way appropriate to the page and market.",
  ),
] as const satisfies readonly ContentFitnessSubcriterion[];
